/**
 * BADU — mega-menu navigation
 *
 * Opens a panel on hover (pointer devices) and on click/keyboard everywhere.
 * The `hidden` attribute is the single source of truth for panel state, so a
 * panel is genuinely removed from the accessibility tree while closed rather
 * than just visually hidden.
 */
(function () {
  const OPEN_DELAY = 60;
  const CLOSE_DELAY = 180;

  /** Pointer devices get hover-to-open; touch devices must not, or the first
   *  tap would both open the panel and follow the link underneath. */
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  function init(root) {
    const nav = (root instanceof Element ? root : document).querySelector('.badu-nav');
    if (!nav || nav.dataset.baduNavBound === 'true') return;
    nav.dataset.baduNavBound = 'true';

    const entries = Array.from(nav.querySelectorAll('.badu-nav__entry')).filter((entry) =>
      entry.querySelector('.badu-nav__panel')
    );
    if (entries.length === 0) return;

    let openTimer = null;
    let closeTimer = null;

    /**
     * The panel is fixed to the viewport rather than absolutely positioned,
     * because the nav now sits inside a sticky header: an absolute panel would
     * be trapped by whichever header column happened to be positioned, and
     * would come out one column wide instead of full width.
     *
     * Measuring the header's bottom edge on open keeps the panel attached to
     * it through every sticky and transparent state.
     */
    const header = document.querySelector('#header-component');

    function positionPanel(panel) {
      const bottom = header ? header.getBoundingClientRect().bottom : 0;
      panel.style.top = `${Math.max(0, bottom)}px`;
    }

    function repositionOpen() {
      const open = entries.find((entry) => entry.classList.contains('is-open'));
      if (open) positionPanel(open.querySelector('.badu-nav__panel'));
    }

    window.addEventListener('scroll', repositionOpen, { passive: true });
    window.addEventListener('resize', repositionOpen, { passive: true });

    function setOpen(entry, open) {
      const trigger = entry.querySelector('.badu-nav__trigger');
      const panel = entry.querySelector('.badu-nav__panel');
      if (!trigger || !panel) return;

      if (open) {
        // Only one panel at a time.
        for (const other of entries) {
          if (other !== entry) setOpen(other, false);
        }
      }

      if (open) positionPanel(panel);
      panel.hidden = !open;
      trigger.setAttribute('aria-expanded', String(open));
      entry.classList.toggle('is-open', open);
      nav.classList.toggle('has-open-panel', open);
    }

    function closeAll() {
      for (const entry of entries) setOpen(entry, false);
      nav.classList.remove('has-open-panel');
    }

    function clearTimers() {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    }

    for (const entry of entries) {
      const trigger = entry.querySelector('.badu-nav__trigger');

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        clearTimers();
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        setOpen(entry, !isOpen);
      });

      entry.addEventListener('mouseenter', () => {
        if (!canHover.matches) return;
        clearTimers();
        openTimer = window.setTimeout(() => setOpen(entry, true), OPEN_DELAY);
      });

      entry.addEventListener('mouseleave', () => {
        if (!canHover.matches) return;
        clearTimers();
        // A short grace period so crossing the gap to the panel doesn't close it.
        closeTimer = window.setTimeout(closeAll, CLOSE_DELAY);
      });
    }

    // Escape closes and returns focus to the trigger that opened the panel.
    nav.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = entries.find((entry) => entry.classList.contains('is-open'));
      if (!open) return;
      event.stopPropagation();
      setOpen(open, false);
      open.querySelector('.badu-nav__trigger')?.focus();
    });

    // Tabbing out of the nav entirely closes whatever is open.
    nav.addEventListener('focusout', (event) => {
      if (nav.contains(event.relatedTarget)) return;
      closeAll();
    });

    document.addEventListener('click', (event) => {
      if (nav.contains(event.target)) return;
      closeAll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  // The theme editor replaces section markup in place.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
